import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Pencil, ExternalLink, User, Phone, Mail, Calendar, DollarSign, Home } from 'lucide-react';
import { updateTenant } from '@/lib/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const EDITABLE_FIELDS = [
  { key: 'name', label: 'Name', type: 'text' },
  { key: 'phone', label: 'Phone', type: 'text' },
  { key: 'email', label: 'Email', type: 'email' },
  { key: 'move_in_date', label: 'Move In', type: 'date' },
  { key: 'move_out_date', label: 'Move Out', type: 'date' },
  { key: 'monthly_rent', label: 'Monthly Rent', type: 'number' },
  { key: 'deposit_amount', label: 'Deposit Amount', type: 'number' },
  { key: 'payment_method', label: 'Payment Method', type: 'text' },
  { key: 'notes', label: 'Notes', type: 'text' },
  { key: 'is_airbnb_vrbo', label: 'Airbnb/VRBO', type: 'toggle' },
  { key: 'pets', label: 'Pets', type: 'text' },
  { key: 'parking', label: 'Parking', type: 'text' },
];

export function TenantEditPopup({ open, onClose, tenant, onUpdated }) {
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    if (tenant) {
      setForm({ ...tenant });
      setEditing(false);
    }
  }, [tenant]);

  if (!tenant) return null;

  const handleFieldChange = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const handleSaveClick = () => {
    setShowConfirm(true);
  };

  const handleConfirmSave = async () => {
    setShowConfirm(false);
    setSaving(true);
    try {
      await updateTenant(tenant.id, form);
      toast.success('Tenant updated');
      setEditing(false);
      if (onUpdated) onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update tenant');
    } finally {
      setSaving(false);
    }
  };

  const handleGoToTenant = () => {
    onClose();
    navigate(`/tenants?highlight=${tenant.id}`);
  };

  const handleCancel = () => {
    setForm({ ...tenant });
    setEditing(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-hidden flex flex-col" data-testid="tenant-edit-popup">
          <DialogHeader>
            <div className="flex items-center justify-between pr-6">
              <div>
                <DialogTitle className="text-lg font-heading flex items-center gap-2">
                  <User className="h-4 w-4" />
                  {tenant.name}
                </DialogTitle>
                <DialogDescription className="flex items-center gap-2 mt-0.5">
                  {tenant.unit_number && <span>Unit {tenant.unit_number}</span>}
                  {tenant.property_name && <span>· {tenant.property_name}</span>}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <ScrollArea className="flex-1 -mx-6 px-6">
            <div className="space-y-3 pb-4">
              {!editing ? (
                <>
                  {/* View Mode */}
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <InfoItem icon={Phone} label="Phone" value={tenant.phone} />
                    <InfoItem icon={Mail} label="Email" value={tenant.email} />
                    <InfoItem icon={Calendar} label="Move In" value={tenant.move_in_date} />
                    <InfoItem icon={Calendar} label="Move Out" value={tenant.move_out_date} />
                    <InfoItem icon={DollarSign} label="Monthly Rent" value={tenant.monthly_rent ? `$${Number(tenant.monthly_rent).toLocaleString()}` : null} />
                    <InfoItem icon={DollarSign} label="Deposit" value={tenant.deposit_amount ? `$${Number(tenant.deposit_amount).toLocaleString()}` : null} />
                    <InfoItem icon={Home} label="Payment" value={tenant.payment_method} />
                    <div className="col-span-2">
                      <InfoItem label="Notes" value={tenant.notes} />
                    </div>
                  </div>
                  {tenant.is_airbnb_vrbo && (
                    <Badge variant="secondary" className="text-xs">Airbnb/VRBO</Badge>
                  )}
                  {tenant.pets && (
                    <div className="text-xs text-muted-foreground">Pets: {tenant.pets}</div>
                  )}
                </>
              ) : (
                <>
                  {/* Edit Mode */}
                  <div className="space-y-3">
                    {EDITABLE_FIELDS.map(f => (
                      <div key={f.key}>
                        {f.type === 'toggle' ? (
                          <div className="flex items-center justify-between">
                            <Label className="text-xs">{f.label}</Label>
                            <Switch
                              checked={!!form[f.key]}
                              onCheckedChange={(val) => handleFieldChange(f.key, val)}
                              data-testid={`tenant-edit-${f.key}`}
                            />
                          </div>
                        ) : (
                          <>
                            <Label className="text-xs text-muted-foreground">{f.label}</Label>
                            <Input
                              type={f.type}
                              value={form[f.key] || ''}
                              onChange={(e) => handleFieldChange(f.key, f.type === 'number' ? parseFloat(e.target.value) || '' : e.target.value)}
                              className="h-8 text-sm mt-0.5"
                              data-testid={`tenant-edit-${f.key}`}
                            />
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </ScrollArea>

          <Separator />
          <DialogFooter className="flex-row gap-2 sm:justify-between">
            <Button variant="outline" size="sm" onClick={handleGoToTenant} className="gap-1.5" data-testid="tenant-popup-go-to-page">
              <ExternalLink className="h-3.5 w-3.5" /> Go to Tenant
            </Button>
            <div className="flex gap-2">
              {editing ? (
                <>
                  <Button variant="ghost" size="sm" onClick={handleCancel} data-testid="tenant-popup-cancel">Cancel</Button>
                  <Button size="sm" onClick={handleSaveClick} disabled={saving} data-testid="tenant-popup-save">
                    {saving ? 'Saving...' : 'Save'}
                  </Button>
                </>
              ) : (
                <Button variant="outline" size="sm" onClick={() => setEditing(true)} className="gap-1.5" data-testid="tenant-popup-edit">
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation dialog */}
      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Save Changes?</AlertDialogTitle>
            <AlertDialogDescription>Are you sure you want to save the changes to {tenant.name}?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="tenant-save-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmSave} data-testid="tenant-save-confirm">Save</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function InfoItem({ icon: Icon, label, value }) {
  return (
    <div>
      <p className="text-[10px] text-muted-foreground uppercase tracking-wide flex items-center gap-1">
        {Icon && <Icon className="h-3 w-3" />} {label}
      </p>
      <p className="text-sm font-medium mt-0.5">{value || '\u2014'}</p>
    </div>
  );
}
